import type { Customer, LogEntry, User } from '../types';

export type ViewKey = 'all' | 'today' | 'targets' | 'pipeline' | 'quotes' | 'mgmt';

interface Props {
  user: User;
  customers: Customer[];
  logs: LogEntry[];
  visibleCustomers: Customer[];
  view: ViewKey;
  filterCustomer: string | null;
  filterAM: string | null;
  open: boolean;
  onSetView: (v: ViewKey) => void;
  onSelectCustomer: (id: string | null) => void;
  onSetAMFilter: (am: string | null) => void;
  onCloseSidebar: () => void;
  onAddCustomer: () => void;
}

export function Sidebar(props: Props) {
  const {
    user, customers, logs, visibleCustomers, view, filterCustomer, filterAM,
    open, onSetView, onSelectCustomer, onSetAMFilter, onCloseSidebar, onAddCustomer
  } = props;

  const isAdmin = user.role === 'admin';
  const ams = isAdmin ? [...new Set(customers.map(c => c.amName).filter(Boolean))].sort() : [];

  const navAllActive = view === 'all' && filterCustomer === null;
  const navTodayActive = view === 'today' && filterCustomer === null;
  const navTargetsActive = view === 'targets' && filterCustomer === null;
  const navPipelineActive = view === 'pipeline' && filterCustomer === null;
  const navQuotesActive = view === 'quotes' && filterCustomer === null;
  const navMgmtActive = view === 'mgmt' && filterCustomer === null;

  return (
    <>
      <div className={`sidebar-overlay ${open ? 'open' : ''}`} onClick={onCloseSidebar} />
      <div className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-section">
          <div className="sidebar-title">Views</div>
          <div className={`nav-item ${navAllActive ? 'active' : ''}`} onClick={() => onSetView('all')}>
            <span className="nav-icon">📋</span> All Activity
          </div>
          <div className={`nav-item ${navTodayActive ? 'active' : ''}`} onClick={() => onSetView('today')}>
            <span className="nav-icon">📅</span> Today
          </div>
          <div className={`nav-item ${navTargetsActive ? 'active' : ''}`} onClick={() => onSetView('targets')}>
            <span className="nav-icon">🎯</span> Today's Targets
          </div>
          <div className={`nav-item ${navPipelineActive ? 'active' : ''}`} onClick={() => onSetView('pipeline')}>
            <span className="nav-icon">📋</span> My Pipeline
          </div>
          <div className={`nav-item ${navQuotesActive ? 'active' : ''}`} onClick={() => onSetView('quotes')}>
            <span className="nav-icon">🏆</span> Won / Lost Quotes
          </div>
          {isAdmin && (
            <div className={`nav-item ${navMgmtActive ? 'active' : ''}`} onClick={() => onSetView('mgmt')}>
              <span className="nav-icon">⚙️</span> Management
            </div>
          )}
        </div>

        <div className="sidebar-section flex-grow">
          <div className="sidebar-title">Customers</div>
          <div className="customer-list">
            {isAdmin && ams.length > 0 && (
              <div className="am-section">
                <div className="sidebar-title am-section-title">Account Managers</div>
                <div
                  className={`customer-item ${!filterAM ? 'active' : ''}`}
                  onClick={() => onSetAMFilter(null)}
                  style={{ fontSize: 12 }}
                >
                  <span>All Accounts</span>
                  <span className="customer-badge">{customers.length}</span>
                </div>
                {ams.map((am) => {
                  const amCusts = customers.filter(c => c.amName === am);
                  const amLogs = logs.filter(l => amCusts.some(c => c.id === l.customerId));
                  const calls = amLogs.filter(l => l.type === 'call').length;
                  const emails = amLogs.filter(l => l.type === 'email').length;
                  const notes = amLogs.filter(l => l.type === 'note').length;
                  return (
                    <div
                      key={am}
                      className={`customer-item am-stack ${filterAM === am ? 'active' : ''}`}
                      onClick={() => onSetAMFilter(am)}
                    >
                      <div className="am-stack-row">
                        <span className="am-stack-name">{am}</span>
                        <span className="customer-badge">{amCusts.length}</span>
                      </div>
                      <div className="am-stack-counts">
                        <span className="c-call">📞{calls}</span>
                        <span className="c-email">✉️{emails}</span>
                        <span className="c-note">📝{notes}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div
              className={`customer-item ${filterCustomer === null ? 'active' : ''}`}
              onClick={() => onSelectCustomer(null)}
            >
              <span>All Customers</span>
              <span className="customer-badge">{visibleCustomers.length}</span>
            </div>

            {visibleCustomers.map((c) => {
              const count = logs.filter(l => l.customerId === c.id).length;
              return (
                <div
                  key={c.id}
                  className={`customer-item ${filterCustomer === c.id ? 'active' : ''}`}
                  onClick={() => onSelectCustomer(c.id)}
                  title={c.name}
                >
                  <span className="customer-name-truncate">{c.name}</span>
                  <span className="customer-badge">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {(isAdmin || user.role === 'am') && (
          <button className="add-customer-btn" onClick={onAddCustomer}>+ Add Customer</button>
        )}
      </div>
    </>
  );
}
